import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Layers,
  Link2,
  Moon,
  Plus,
  Search,
  Sun,
  type LucideIcon,
} from 'lucide-react';

import { SIDEBAR_NAV } from '../layout/AppSidebar';
import { useTheme, type Theme } from '../../hooks/useTheme';
import { cn } from '../../lib/utils';

/**
 * Command palette (⌘K) — hand-rolled on Clarity tokens (FE-L5), no `cmdk` dep.
 * Mounted ONCE at the app root; the ⌘K (⌃K on Windows) listener lives here and
 * nowhere else. A focus-trapped role=dialog that restores focus on esc.
 *
 * Typed Command[] registry with four kinds (navigate / create / search /
 * toggle); navigate rows are generated from the route table (SIDEBAR_NAV) so a
 * new route appears in ⌘K for free — the palette never hard-codes a route.
 * Section order is fixed: entities → create → navigate. Live entity search
 * (clients/txns/invoices) plugs in as `search`-kind commands; the registry +
 * dispatch are ready for it.
 */

export type CommandKind = 'navigate' | 'create' | 'toggle' | 'search';

export interface Command {
  id: string;
  kind: CommandKind;
  label: string;
  sub?: string;
  icon: LucideIcon;
  /** Whether running this command should keep the palette open (toggles do). */
  keepOpen?: boolean;
  run: () => void;
}

interface Section {
  name: string;
  rows: Command[];
}

export interface CommandPaletteProps {
  /** Force-open for tests/stories; normally self-managed via ⌘K. */
  defaultOpen?: boolean;
}

/**
 * Window event any host can dispatch to open the palette (e.g. the topbar
 * ⌘K chip), so the open trigger stays here — the single owner of open state.
 */
export const COMMAND_PALETTE_OPEN_EVENT = 'fhq:open-command-palette';

export function CommandPalette({ defaultOpen = false }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const listboxId = useId();

  const close = useCallback(() => setOpen(false), []);

  // ⌘K / ⌃K toggles from any in-app surface; a host (the topbar chip) can also
  // open it via the COMMAND_PALETTE_OPEN_EVENT. Both listeners live only here.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    const onOpenRequest = () => setOpen(true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpenRequest);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, onOpenRequest);
    };
  }, []);

  // Focus management — trap on open, restore the prior element on close.
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = document.activeElement as HTMLElement | null;
      setQuery('');
      setActiveIndex(0);
      // Focus the input after the dialog mounts.
      const raf = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
    restoreFocusRef.current?.focus?.();
    return undefined;
  }, [open]);

  const commands = useMemo<{ navigate: Command[]; actions: Command[] }>(() => {
    const navCommands: Command[] = SIDEBAR_NAV.flatMap((group) =>
      group.items.map((item) => ({
        id: `nav-${item.id}`,
        kind: 'navigate' as const,
        label: `Go to ${item.label}`,
        sub: item.href,
        icon: item.icon,
        run: () => navigate(item.href),
      })),
    );

    const actionCommands: Command[] = [
      {
        id: 'create-transaction',
        kind: 'create',
        label: 'Add transaction',
        sub: 'Create',
        icon: Plus,
        run: () => navigate('/transactions'),
      },
      {
        id: 'create-invoice',
        kind: 'create',
        label: 'Create invoice',
        sub: 'Create',
        icon: FileText,
        run: () => navigate('/invoices'),
      },
      {
        id: 'connect-bank',
        kind: 'create',
        label: 'Connect a bank account',
        sub: '/bank-connections',
        icon: Link2,
        run: () => navigate('/bank-connections'),
      },
      {
        id: 'toggle-theme',
        kind: 'toggle',
        label: 'Cycle appearance (Mixed · Light · Dark)',
        sub: 'Appearance',
        icon: theme === 'mixed' ? Layers : theme === 'light' ? Sun : Moon,
        keepOpen: true,
        run: () => {
          const order: Theme[] = ['mixed', 'light', 'dark'];
          setTheme(order[(order.indexOf(theme) + 1) % order.length]);
        },
      },
    ];

    return { navigate: navCommands, actions: actionCommands };
  }, [navigate, theme, setTheme]);

  // Section order is fixed: entities → create → navigate.
  const sections = useMemo<Section[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return [
        { name: 'Jump to', rows: commands.navigate },
        { name: 'Actions', rows: commands.actions },
      ];
    }
    const match = (c: Command) => c.label.toLowerCase().includes(q);
    return [
      { name: 'Actions', rows: commands.actions.filter(match) },
      { name: 'Jump to', rows: commands.navigate.filter(match) },
    ].filter((s) => s.rows.length > 0);
  }, [query, commands]);

  const flatRows = useMemo(() => sections.flatMap((s) => s.rows), [sections]);

  // Clamp the active row when the result set shrinks.
  useEffect(() => {
    setActiveIndex((i) => (flatRows.length === 0 ? 0 : Math.min(i, flatRows.length - 1)));
  }, [flatRows.length]);

  const runCommand = useCallback(
    (command: Command) => {
      command.run();
      if (!command.keepOpen) close();
    },
    [close],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      // Only the input is focusable — keep focus trapped inside the dialog.
      e.preventDefault();
      inputRef.current?.focus();
      return;
    }
    if (flatRows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatRows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatRows.length) % flatRows.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = flatRows[activeIndex];
      if (row) runCommand(row);
    }
  };

  if (!open) return null;

  let rowIndex = -1;

  return (
    <div
      className="fixed inset-0 z-modal flex justify-center bg-fg-primary/40 px-4 pt-[12vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="h-fit w-full max-w-[620px] overflow-hidden rounded-xl border border-border bg-bg-primary shadow-lg"
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2.5 border-b border-border-subtle px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-fg-secondary" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={
              flatRows[activeIndex] ? `${listboxId}-${flatRows[activeIndex].id}` : undefined
            }
            aria-label="Search commands and pages"
            placeholder="Search or jump to…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            className="flex-1 bg-transparent text-body text-fg-primary placeholder:text-fg-secondary focus:outline-none"
          />
        </div>

        {flatRows.length === 0 ? (
          <div className="px-4 py-8 text-center text-small text-fg-secondary">
            No matches. Try a client name, an amount, or “add”.
          </div>
        ) : (
          <ul id={listboxId} role="listbox" aria-label="Results" className="max-h-[60vh] overflow-y-auto py-1.5">
            {sections.map((section) => (
              <li key={section.name} role="presentation">
                <div className="px-4 pb-1 pt-2 text-micro font-semibold uppercase tracking-wide text-fg-secondary">
                  {section.name}
                </div>
                <ul role="presentation">
                  {section.rows.map((row) => {
                    rowIndex += 1;
                    const isActive = rowIndex === activeIndex;
                    const Icon = row.icon;
                    return (
                      <li
                        key={row.id}
                        id={`${listboxId}-${row.id}`}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActiveIndex(flatRows.indexOf(row))}
                        onClick={() => runCommand(row)}
                        className={cn(
                          'mx-1.5 flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2',
                          isActive ? 'bg-bg-tertiary' : '',
                        )}
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-bg-tertiary text-forest">
                          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-small font-medium text-fg-primary">
                            {row.label}
                          </span>
                          {row.sub ? (
                            <span className="block truncate text-micro text-fg-secondary">
                              {row.sub}
                            </span>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
